import React from 'react';
import { UserStatus, User, FnsAuthResult } from '../types';
import { userService } from '../services/userService';
import { ReactComponent as AvatarIcon } from '../icons/avatar.svg';
import { ReactComponent as AvatarVerifiedIcon } from '../icons/avatar_verified.svg';
import { ReactComponent as AvatarSelfIcon } from '../icons/avatar_self.svg';

interface ProfileStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: User;
  onUserUpdate?: (user: User) => void;
}

const ProfileStatusModal: React.FC<ProfileStatusModalProps> = ({
  isOpen,
  onClose,
  user,
  onUserUpdate
}) => {
  const [showVerificationOptions, setShowVerificationOptions] = React.useState(false);
  const [showVerificationPanel, setShowVerificationPanel] = React.useState(false);
  const [showSelfemployedPanel, setShowSelfemployedPanel] = React.useState(false);
  const [showSmsCodePanel, setShowSmsCodePanel] = React.useState(false);
  const [showBirthdayPanel, setShowBirthdayPanel] = React.useState(false);
  const [showMtsSmsCodePanel, setShowMtsSmsCodePanel] = React.useState(false);
  const [challengeToken, setChallengeToken] = React.useState<string>('');
  const [smsCode, setSmsCode] = React.useState<string>('');
  const [mtsSmsCode, setMtsSmsCode] = React.useState<string>('');
  const [birthday, setBirthday] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [successMessage, setSuccessMessage] = React.useState<string>('');
  const getStatusIcon = (status: UserStatus) => {
    switch (status) {
      case UserStatus.Anonymous:
        return (
          <div className="w-12 h-12 bg-[#2e3036] rounded-full flex items-center justify-center">
            <AvatarIcon className="w-6 h-6 text-white" />
          </div>
        );
      case UserStatus.Verified:
        return (
          <div className="w-12 h-12 bg-[#2e3036] rounded-full flex items-center justify-center">
            <AvatarVerifiedIcon className="w-6 h-6 text-white" />
          </div>
        );
      case UserStatus.Selfemployed:
        return (
          <div className="w-12 h-12 bg-[#2e3036] rounded-full flex items-center justify-center">
            <AvatarSelfIcon className="w-6 h-6 text-white" />
          </div>
        );
      default:
        return (
          <div className="w-12 h-12 bg-[#2e3036] rounded-full flex items-center justify-center">
            <AvatarIcon className="w-6 h-6 text-white" />
          </div>
        );
    }
  };

  const getStatusInfo = (status: UserStatus) => {
    switch (status) {
      case UserStatus.Anonymous:
        return {
          title: 'Аноним',
          description: 'Ваш текущий статус',
          info: 'На данном статусе лимит в сутки ограничен до 15 000 ₽'
        };
      case UserStatus.Verified:
        return {
          title: 'Проверенный',
          description: 'Ваш текущий статус',
          info: 'Для данного статуса необходимо подтвердить паспортные данные. Лимит до 50 000 ₽'
        };
      case UserStatus.Selfemployed:
        return {
          title: 'Самозанятый',
          description: 'Ваш текущий статус',
          info: 'Подтвердив статус через "Мой налог", вы сможете автоматически получать чек НПД после закрытия сделки. Лимит до 200 000 ₽ в месяц'
        };
      default:
        return {
          title: 'Неизвестно',
          description: 'Ваш текущий статус',
          info: 'Статус не определен'
        };
    }
  };

  const getAvailableStatuses = () => {
    const statuses = [];
    
    if (user?.status === UserStatus.Anonymous) {
      statuses.push(
        {
          status: UserStatus.Verified,
          title: 'Проверенный',
          description: 'Ваш текущий статус',
          info: 'Для данного статуса необходимо подтвердить паспортные данные. Лимит до 50 000 ₽',
          canUpgrade: true,
          upgradeText: 'Подтвердить статус'
        },
        {
          status: UserStatus.Selfemployed,
          title: 'Самозанятый',
          description: 'Ваш текущий статус',
          info: 'Подтвердив статус через "Мой налог", вы сможете автоматически получать чек НПД после закрытия сделки. Лимит до 200 000 ₽ в месяц',
          canUpgrade: true,
          upgradeText: 'Подтвердить статус'
        }
      );
    } else if (user?.status === UserStatus.Verified) {
      statuses.push({
        status: UserStatus.Selfemployed,
        title: 'Самозанятый',
        description: 'Ваш текущий статус',
        info: 'Подтвердив статус через "Мой налог", вы сможете автоматически получать чек НПД после закрытия сделки. Лимит до 200 000 ₽ в месяц',
        canUpgrade: true,
        upgradeText: 'Подтвердить статус'
      });
    }
    
    return statuses;
  };

  const handleUpgradeStatus = (status: UserStatus) => {
    if (status === UserStatus.Verified) {
      setShowVerificationPanel(true);
    } else if (status === UserStatus.Selfemployed) {
      setShowSelfemployedPanel(true);
    } else {
      // TODO: Реализовать логику повышения статуса для других статусов
      console.log('Upgrading to status:', status);
    }
  };

  const handleFnsToggle = async () => {
    console.log('handleFnsToggle called, user:', user);
    if (!user) {
      console.log('No user, returning');
      return;
    }
    
    try {
      const newValue = !user.isFnsEnabled;
      console.log('Calling userService.toggleFnsEnabled with:', newValue);
      
      await userService.toggleFnsEnabled(newValue);
      console.log('API call successful');
      
      // Обновляем пользователя локально
      const updatedUser = { ...user, isFnsEnabled: newValue };
      console.log('Updated user:', updatedUser);
      
      if (onUserUpdate) {
        console.log('Calling onUserUpdate');
        onUserUpdate(updatedUser);
      } else {
        console.log('onUserUpdate not provided');
      }
    } catch (error) {
      console.error('Error toggling FNS enabled:', error);
      // TODO: Показать уведомление об ошибке
    }
  };

  const handleVerifyByPassport = async () => {
    setIsLoading(true);
    setErrorMessage('');
    
    try {
      const result = await userService.initiateRimScan();
      
      if (result.url) {
        // Переходим на полученный URL
        window.location.href = result.url;
      } else {
        // Показываем ошибку если URL не задан
        setErrorMessage('Не удалось получить ссылку для сканирования паспорта');
      }
    } catch (error) {
      console.error('Error initiating RIM scan:', error);
      setErrorMessage('Ошибка при инициации сканирования паспорта');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyByPhone = () => {
    setShowVerificationPanel(false);
    setShowBirthdayPanel(true);
  };

  // Функция для форматирования даты в маску "дд.мм.гггг"
  const formatBirthdayInput = (value: string): string => {
    // Убираем все, кроме цифр
    const digits = value.replace(/\D/g, '');
    
    // Форматируем по маске дд.мм.гггг
    if (digits.length <= 2) {
      return digits;
    } else if (digits.length <= 4) {
      return `${digits.slice(0, 2)}.${digits.slice(2)}`;
    } else if (digits.length <= 8) {
      return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
    } else {
      return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4, 8)}`;
    }
  };

  // Функция для конвертации даты из "дд.мм.гггг" в "yyyy-MM-dd"
  const convertBirthdayFormat = (birthday: string): string => {
    const parts = birthday.split('.');
    if (parts.length === 3) {
      const day = parts[0].padStart(2, '0');
      const month = parts[1].padStart(2, '0');
      const year = parts[2];
      return `${year}-${month}-${day}`;
    }
    return '';
  };

  // Обработчик изменения даты рождения
  const handleBirthdayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formatted = formatBirthdayInput(value);
    setBirthday(formatted);
  };

  // Обработчик подтверждения даты рождения
  const handleConfirmBirthday = async () => {
    if (!birthday || birthday.split('.').length !== 3) {
      setErrorMessage('Введите корректную дату рождения');
      return;
    }

    const birthdayFormatted = convertBirthdayFormat(birthday);
    if (!birthdayFormatted) {
      setErrorMessage('Неверный формат даты');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      await userService.verifyByPhone(birthdayFormatted);
      
      // Успешно! Теперь показываем панель ввода SMS кода от MTS
      setShowBirthdayPanel(false);
      setShowMtsSmsCodePanel(true);
      
      // Сбрасываем состояние
      setBirthday('');
      setErrorMessage('');
      
    } catch (error: any) {
      console.error('Error verifying by phone:', error);
      if (error.response?.status === 400) {
        setErrorMessage('Неверная дата рождения. Проверьте введенные данные.');
      } else {
        setErrorMessage('Ошибка при подтверждении. Попробуйте еще раз.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatPhone = (phone: string | number): string => {
    if (!phone) return '';
    // Форматируем номер в формате +7 (999) 123-45-67
    const phoneStr = phone.toString();
    const cleaned = phoneStr.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return `+${cleaned[0]} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7, 9)}-${cleaned.slice(9)}`;
    }
    return phoneStr;
  };

  const handleSelfemployedNext = async () => {
    setIsLoading(true);
    setErrorMessage('');
    
    try {
      const result = await userService.initiateFnsAuth();
      
      if (result.message) {
        // Ошибка от бэкенда
        setErrorMessage(result.message);
        setIsLoading(false);
        return;
      }
      
      if (result.challengeToken) {
        // Успешно, показываем форму ввода кода
        setChallengeToken(result.challengeToken);
        setShowSelfemployedPanel(false);
        setShowSmsCodePanel(true);
      }
    } catch (error) {
      console.error('Error initiating FNS auth:', error);
      setErrorMessage('Ошибка при отправке SMS. Попробуйте еще раз.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifySmsCode = async () => {
    if (!smsCode.trim()) {
      setErrorMessage('Введите код из SMS');
      return;
    }
    
    setIsLoading(true);
    setErrorMessage('');
    
    try {
      await userService.verifyFnsCode(challengeToken, smsCode.trim());
      
      // Успешно! Обновляем информацию о пользователе
      if (onUserUpdate && user) {
        const updatedUser = await userService.getCurrentUser();
        onUserUpdate(updatedUser);
      }
      
      // Закрываем панели SMS и самозанятого, но оставляем основную панель открытой
      setShowSmsCodePanel(false);
      setShowSelfemployedPanel(false);
      
      // Сбрасываем состояния
      setChallengeToken('');
      setSmsCode('');
      setErrorMessage('');
      
      // Показываем сообщение об успехе
      setSuccessMessage('Статус успешно обновлен! Теперь вы можете формировать чеки НПД.');
      
      // Автоматически скрываем сообщение через 5 секунд
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      
    } catch (error: any) {
      console.error('Error verifying SMS code:', error);
      if (error.response?.status === 400) {
        setErrorMessage('Неверный код. Попробуйте еще раз.');
      } else {
        setErrorMessage('Ошибка при подтверждении кода. Попробуйте еще раз.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Обработчик подтверждения SMS кода от MTS
  const handleVerifyMtsSmsCode = async () => {
    if (!mtsSmsCode.trim()) {
      setErrorMessage('Введите код из SMS');
      return;
    }
    
    setIsLoading(true);
    setErrorMessage('');
    
    try {
      await userService.verifyMtsCode(mtsSmsCode.trim());
      
      // Успешно! Обновляем информацию о пользователе
      if (onUserUpdate && user) {
        const updatedUser = await userService.getCurrentUser();
        onUserUpdate(updatedUser);
      }
      
      // Закрываем панель SMS кода от MTS
      setShowMtsSmsCodePanel(false);
      
      // Сбрасываем состояния
      setMtsSmsCode('');
      setErrorMessage('');
      
      // Показываем сообщение об успехе
      setSuccessMessage('Статус успешно обновлен! Теперь вы можете создавать сделки с повышенным лимитом.');
      
      // Автоматически скрываем сообщение через 5 секунд
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      
    } catch (error: any) {
      console.error('Error verifying MTS SMS code:', error);
      if (error.response?.status === 400) {
        setErrorMessage('Неверный код. Попробуйте еще раз.');
      } else {
        setErrorMessage('Ошибка при подтверждении кода. Попробуйте еще раз.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const currentStatusInfo = getStatusInfo(user?.status || UserStatus.Anonymous);
  const availableStatuses = getAvailableStatuses();

  // Логирование для отладки
  console.log('ProfileStatusModal render:', {
    isOpen,
    userStatus: user?.status,
    user,
    userIsFnsEnabled: user?.isFnsEnabled
  });

  React.useEffect(() => {
    if (!isOpen) {
      setShowVerificationOptions(false);
      setShowVerificationPanel(false);
      setShowSelfemployedPanel(false);
      setShowSmsCodePanel(false);
      setShowBirthdayPanel(false);
      setShowMtsSmsCodePanel(false);
      setChallengeToken('');
      setSmsCode('');
      setMtsSmsCode('');
      setBirthday('');
      setErrorMessage('');
      setSuccessMessage('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-50 transition-opacity ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />
        
        {/* Modal */}
        <div className={`fixed bottom-0 left-0 right-0 bg-[#141826] rounded-t-3xl w-full max-h-screen flex flex-col transform transition-transform duration-300 ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}>
          {/* Header */}
          <div className="text-white text-left p-4 relative flex-shrink-0">
            <button
              onClick={onClose}
              className="absolute right-4 top-4 w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-white hover:bg-slate-600 transition-colors"
            >
              ✕
            </button>
            <div className="pt-12">
              <h3 className="text-2xl">Статус профиля</h3>
              <p className="text-slate-400 text-sm mt-2">
                Влияет на лимиты, получения чека после проведения оплаты
              </p>
              
              {/* Сообщение об успешном обновлении статуса */}
              {successMessage && (
                <div className="bg-green-500/20 border border-green-500/30 rounded-2xl p-3 mt-4">
                  <div className="text-green-400 text-sm">{successMessage}</div>
                </div>
              )}
            </div>
          </div>
          
          {/* Body */}
          <div className="text-white px-4 pb-6 overflow-y-auto">
            {/* Активный статус */}
            <div className="mb-8">
              <h4 className="text-xl mb-4">Активный статус</h4>
              <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-4">
                <div className="flex items-center space-x-3 mb-3">
                  {getStatusIcon(user?.status || UserStatus.Anonymous)}
                  <div>
                    <h5 className="text-lg">{currentStatusInfo.title}</h5>
                    <p className="text-slate-400 text-sm">{currentStatusInfo.description}</p>
                  </div>
                </div>
                <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                      <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                        <span className="text-gray-800 text-sm">i</span>
                      </div>
                    </div>
                    <div className="flex-1 text-white text-sm leading-tight">
                      {currentStatusInfo.info}
                    </div>
                  </div>
                  
                  {/* Чекбокс для формирования чеков НПД (только для самозанятых) */}
                  {user?.status === UserStatus.Selfemployed && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id="fns-enabled"
                            checked={user?.status === UserStatus.Selfemployed && user?.isFnsEnabled}
                            onChange={handleFnsToggle}
                            className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                          />
                          <label htmlFor="fns-enabled" className="text-white text-sm">
                            Формировать чеки НПД<br/>в "Мой налог" ФНС
                          </label>
                        </div>
                        <div className="text-slate-400 text-xs">
                          {user?.status === UserStatus.Selfemployed && user?.isFnsEnabled ? 'Включено' : 'Выключено'}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Можете получить */}
            {availableStatuses.length > 0 && (
              <div>
                <h5 className="text-lg mb-4">Можете получить</h5>
                <div className="space-y-4">
                  {availableStatuses.map((statusInfo) => (
                    <div key={statusInfo.status} className="bg-white/5 backdrop-blur-sm rounded-3xl p-4">
                      <div className="flex items-center space-x-3 mb-3">
                        {getStatusIcon(statusInfo.status)}
                        <div>
                          <h6 className="text-lg">{statusInfo.title}</h6>
                          <p className="text-slate-400 text-sm">{statusInfo.description}</p>
                        </div>
                      </div>
                      <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-4 mb-3">
                        <div className="flex items-start space-x-3">
                          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                              <span className="text-gray-800 text-sm">i</span>
                            </div>
                          </div>
                          <div className="flex-1 text-white text-sm leading-tight">
                            {statusInfo.info}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleUpgradeStatus(statusInfo.status)}
                        className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-white text-gray-800 hover:bg-white/90 active:scale-95"
                      >
                        <span>{statusInfo.upgradeText}</span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {availableStatuses.length === 0 && (
              <div className="text-center text-slate-400">
                <p>Вы достигли максимального статуса!</p>
              </div>
            )}
          </div>
        </div>
      </div>

       {/* Панель выбора способа подтверждения */}
       {showVerificationPanel && (
         <div className="fixed inset-0 z-50 flex items-end justify-center">
           {/* Backdrop */}
           <div 
             className="absolute inset-0 bg-black/50 backdrop-blur-sm"
             onClick={() => setShowVerificationPanel(false)}
           />
           
           {/* Panel */}
           <div className="relative bg-[#141826] rounded-t-3xl w-full max-h-[90vh] overflow-y-auto">
             {/* Header */}
             <div className="text-white text-left p-4 relative border-b border-white/10">
               <button
                 onClick={() => setShowVerificationPanel(false)}
                 className="absolute right-4 top-4 w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-white hover:bg-slate-600 transition-colors"
               >
                 ✕
               </button>
               <h3 className="text-2xl">Подтверждение статуса</h3>
               <p className="text-slate-400 text-sm mt-2">
                 Выберите способ подтверждения
               </p>
             </div>
             
             {/* Body */}
             <div className="text-white p-4">
               <div className="space-y-3">
                 <button
                   onClick={() => handleVerifyByPassport()}
                   className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-white text-gray-800 hover:bg-white/90 active:scale-95"
                 >
                   <span>Отсканировать паспорт</span>
                 </button>
                 
                 <button
                   onClick={() => handleVerifyByPhone()}
                   className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-slate-700 text-white hover:bg-slate-600 active:scale-95"
                 >
                   <span>По номеру телефона</span>
                 </button>
               </div>
             </div>
           </div>
         </div>
       )}

       {/* Панель для статуса "Самозанятый" */}
       {showSelfemployedPanel && (
         <div className="fixed inset-0 z-50 flex items-end justify-center">
           {/* Backdrop */}
           <div 
             className="absolute inset-0 bg-black/50 backdrop-blur-sm"
             onClick={() => setShowSelfemployedPanel(false)}
           />
           
           {/* Panel */}
           <div className="relative bg-[#141826] rounded-t-3xl w-full max-h-[90vh] overflow-y-auto">
             {/* Header */}
             <div className="text-white text-left p-4 relative border-b border-white/10">
               <button
                 onClick={() => setShowSelfemployedPanel(false)}
                 className="absolute right-4 top-4 w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-white hover:bg-slate-600 transition-colors"
               >
                 ✕
               </button>
               <h3 className="text-2xl">Подключение "Мой налог"</h3>
               <p className="text-slate-400 text-sm mt-2">
                 Подтверждение статуса самозанятого
               </p>
             </div>
             
             {/* Body */}
             <div className="text-white p-4">
               <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-4 mb-6">
                 <div className="text-white text-sm leading-tight">
                   Вам будет отправлена SMS на ваш номер телефона{' '}
                   <span className="text-blue-400 font-medium">
                     {user?.phone ? formatPhone(user.phone) : 'не указан'}
                   </span>{' '}
                   от ФНС для подключения "Мой налог"
                 </div>
               </div>
               
               {errorMessage && (
                 <div className="bg-red-500/20 border border-red-500/30 rounded-2xl p-3 mb-4">
                   <div className="text-red-400 text-sm">{errorMessage}</div>
                 </div>
               )}
               
               <button
                 onClick={handleSelfemployedNext}
                 disabled={isLoading}
                 className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-white text-gray-800 hover:bg-white/90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
               >
                 {isLoading ? (
                   <span>Отправка SMS...</span>
                 ) : (
                   <span>Далее</span>
                 )}
               </button>
             </div>
           </div>
         </div>
       )}

       {/* Панель для ввода даты рождения */}
       {showBirthdayPanel && (
         <div className="fixed inset-0 z-50 flex items-end justify-center">
           {/* Backdrop */}
           <div 
             className="absolute inset-0 bg-black/50 backdrop-blur-sm"
             onClick={() => setShowBirthdayPanel(false)}
           />
           
           {/* Panel */}
           <div className="relative bg-[#141826] rounded-t-3xl w-full max-h-[90vh] overflow-y-auto">
             {/* Header */}
             <div className="text-white text-left p-4 relative border-b border-white/10">
               <button
                 onClick={() => setShowBirthdayPanel(false)}
                 className="absolute right-4 top-4 w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-white hover:bg-slate-600 transition-colors"
               >
                 ✕
               </button>
               <h3 className="text-2xl">Введите дату рождения</h3>
               <p className="text-slate-400 text-sm mt-2">
                 Для подтверждения по номеру телефона
               </p>
             </div>
             
             {/* Body */}
             <div className="text-white p-4">
               <div className="mb-6">
                 <label htmlFor="birthday" className="block text-white text-sm mb-2">
                   Дата рождения
                 </label>
                 <input
                   type="text"
                   id="birthday"
                   value={birthday}
                   onChange={handleBirthdayChange}
                   placeholder="дд.мм.гггг"
                   className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:border-white/40 transition-colors"
                   maxLength={10}
                 />
                 <div className="text-slate-400 text-xs mt-2">
                   Формат: дд.мм.гггг (например: 15.08.1990)
                 </div>
               </div>
               
               {errorMessage && (
                 <div className="bg-red-500/20 border border-red-500/30 rounded-2xl p-3 mb-4">
                   <div className="text-red-400 text-sm">{errorMessage}</div>
                 </div>
               )}
               
               <button
                 onClick={handleConfirmBirthday}
                 disabled={isLoading || !birthday || birthday.split('.').length !== 3}
                 className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-white text-gray-800 hover:bg-white/90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
               >
                 {isLoading ? (
                   <span>Подтверждение...</span>
                 ) : (
                   <span>Подтвердить</span>
                 )}
               </button>
             </div>
           </div>
         </div>
       )}

       {/* Панель для ввода SMS кода от MTS */}
       {showMtsSmsCodePanel && (
         <div className="fixed inset-0 z-50 flex items-end justify-center">
           {/* Backdrop */}
           <div 
             className="absolute inset-0 bg-black/50 backdrop-blur-sm"
             onClick={() => setShowMtsSmsCodePanel(false)}
           />
           
           {/* Panel */}
           <div className="relative bg-[#141826] rounded-3xl w-full max-w-sm max-h-[80vh] overflow-y-auto">
             {/* Header */}
             <div className="text-white text-left p-4 relative border-b border-white/10">
               <button
                 onClick={() => setShowMtsSmsCodePanel(false)}
                 className="absolute right-4 top-4 w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-white hover:bg-slate-600 transition-colors"
               >
                 ✕
               </button>
               <h3 className="text-2xl">Введите код из SMS</h3>
               <p className="text-slate-400 text-sm mt-2">
                 Код отправлен на ваш номер телефона от MTS
               </p>
             </div>
             
             {/* Body */}
             <div className="text-white p-4">
               <div className="mb-6">
                 <label htmlFor="mts-sms-code" className="block text-white text-sm mb-2">
                   Код из SMS
                 </label>
                 <input
                   type="text"
                   id="mts-sms-code"
                   value={mtsSmsCode}
                   onChange={(e) => setMtsSmsCode(e.target.value)}
                   placeholder="Введите код"
                   className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-slate-400 focus:outline-none focus:border-white/20"
                   maxLength={6}
                 />
               </div>
               
               {errorMessage && (
                 <div className="bg-red-500/20 border border-red-500/30 rounded-2xl p-3 mb-4">
                   <div className="text-red-400 text-sm">{errorMessage}</div>
                 </div>
               )}
               
               <button
                 onClick={handleVerifyMtsSmsCode}
                 disabled={isLoading || !mtsSmsCode.trim()}
                 className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-white text-gray-800 hover:bg-white/90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
               >
                 {isLoading ? (
                   <span>Подтверждение...</span>
                 ) : (
                   <span>Подтвердить</span>
                 )}
               </button>
             </div>
           </div>
         </div>
       )}

       {/* Панель для ввода SMS кода */}
       {showSmsCodePanel && (
         <div className="fixed inset-0 z-50 flex items-end justify-center">
           {/* Backdrop */}
           <div 
             className="absolute inset-0 bg-black/50 backdrop-blur-sm"
             onClick={() => setShowSmsCodePanel(false)}
           />
           
           {/* Panel */}
           <div className="relative bg-[#141826] rounded-3xl w-full max-h-[90vh] overflow-y-auto">
             {/* Header */}
             <div className="text-white text-left p-4 relative border-b border-white/10">
               <button
                 onClick={() => setShowSmsCodePanel(false)}
                 className="absolute right-4 top-4 w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-white hover:bg-slate-600 transition-colors"
               >
                 ✕
               </button>
               <h3 className="text-2xl">Введите код из SMS</h3>
               <p className="text-slate-400 text-sm mt-2">
                 Код отправлен на ваш номер телефона
               </p>
             </div>
             
             {/* Body */}
             <div className="text-white p-4">
               <div className="mb-6">
                 <label htmlFor="sms-code" className="block text-white text-sm mb-2">
                   Код из SMS
                 </label>
                 <input
                   type="text"
                   id="sms-code"
                   value={smsCode}
                   onChange={(e) => setSmsCode(e.target.value)}
                   placeholder="Введите код"
                   className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-slate-400 focus:outline-none focus:border-white/20"
                   maxLength={6}
                 />
               </div>
               
               {errorMessage && (
                 <div className="bg-red-500/20 border border-red-500/30 rounded-2xl p-3 mb-4">
                   <div className="text-red-400 text-sm">{errorMessage}</div>
                 </div>
               )}
               
               <button
                 onClick={handleVerifySmsCode}
                 disabled={isLoading || !smsCode.trim()}
                 className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-white text-gray-800 hover:bg-white/90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
               >
                 {isLoading ? (
                   <span>Подтверждение...</span>
                 ) : (
                   <span>Подтвердить</span>
                 )}
               </button>
             </div>
           </div>
         </div>
       )}
     </>
   );
};

export default ProfileStatusModal;

